import { useState } from 'react';
import { useScrollReveal } from '../../../hooks/useScrollReveal';

export default function FAQ() {
  const headerReveal = useScrollReveal({ threshold: 0.2 });
  const faq1Reveal = useScrollReveal({ threshold: 0.1 });
  const faq2Reveal = useScrollReveal({ threshold: 0.1 });
  const faq3Reveal = useScrollReveal({ threshold: 0.1 });
  const faq4Reveal = useScrollReveal({ threshold: 0.1 });
  const faq5Reveal = useScrollReveal({ threshold: 0.1 });
  const faq6Reveal = useScrollReveal({ threshold: 0.1 });

  const [openIndex, setOpenIndex] = useState<number | null>(null);

  const faqs = [
    {
      question: 'Kam skirta ši knyga?',
      answer: 'Knyga skirta visiems, kas nori geriau suprasti save ir savo gyvenimo kryptį. Nesvarbu, ar esate studentas, verslininkas, tėvas ar tiesiog ieškote atsakymų – ši knyga jums padės.',
      reveal: faq1Reveal,
    },
    {
      question: 'Ar reikia turėti kokių nors žinių prieš skaitant?',
      answer: 'Ne, knyga parašyta taip, kad ją suprastų bet kas. Nereikia jokių išankstinių žinių apie psichologiją ar savęs pažinimą. Viskas paaiškinta paprastai ir su praktiniais pavyzdžiais.',
      reveal: faq2Reveal,
    },
    {
      question: 'Kiek laiko užtruks perskaityti?',
      answer: 'Vidutiniškai knyga užtrunka 3-4 valandas perskaityti. Tačiau rekomenduojame skaityti lėtai, darant pratimus ir apmąstant kiekvieną skyrių. Taip gausit maksimalią naudą.',
      reveal: faq3Reveal,
    },
    {
      question: 'Ar knyga tik teorinė, ar yra praktinių pratimų?',
      answer: 'Knyga pilna praktinių pratimų ir užduočių. Kiekvienas skyrius baigiasi konkrečiais veiksmais, kuriuos galite atlikti iš karto. Tai ne tik skaitymas – tai transformacijos procesas.',
      reveal: faq4Reveal,
    },
    {
      question: 'Kokiu formatu gausiu knygą?',
      answer: 'Knyga siunčiama PDF formatu į jūsų el. paštą iš karto po apmokėjimo. Galėsite skaityti bet kuriame įrenginyje – telefone, planšetėje ar kompiuteryje.',
      reveal: faq5Reveal,
    },
    {
      question: 'Ar galiu pirkti kaip dovaną?',
      answer: 'Taip! Pirkimo metu galite pasirinkti "Pirkti kaip dovaną" ir įvesti gavėjo duomenis. Knyga bus išsiųsta tiesiogiai jam, o jūs gausite patvirtinimą.',
      reveal: faq6Reveal,
    },
  ];

  const toggleFAQ = (index: number) => {
    setOpenIndex(openIndex === index ? null : index);
  };

  return (
    <section className="py-32 bg-gradient-to-br from-white via-purple-50/30 to-white relative overflow-hidden">
      {/* Decorative elements */}
      <div className="absolute top-20 right-10 w-64 h-64 bg-purple-200/20 rounded-full blur-3xl"></div>
      <div className="absolute bottom-20 left-10 w-80 h-80 bg-pink-200/20 rounded-full blur-3xl"></div>

      <div className="max-w-4xl mx-auto px-6 relative z-10">
        {/* Header */}
        <div 
          ref={headerReveal.ref}
          className={`text-center mb-20 ${headerReveal.isVisible ? 'animate-fade-in-up' : 'opacity-0'}`}
        >
          <div className="inline-block bg-gradient-to-r from-purple-600 to-pink-600 text-white px-6 py-2 rounded-full text-sm font-semibold mb-6">
            ❓ DUK
          </div>
          <h2 className="text-5xl md:text-6xl font-serif font-bold text-gray-900 mb-6">
            Dažniausiai Užduodami Klausimai
          </h2>
          <p className="text-xl text-gray-600 max-w-2xl mx-auto leading-relaxed">
            Atsakymai į jūsų klausimus
          </p>
        </div>

        {/* FAQ Accordion */}
        <div className="space-y-4">
          {faqs.map((faq, index) => (
            <div
              key={index}
              ref={faq.reveal.ref}
              className={`group bg-white rounded-2xl shadow-lg overflow-hidden transition-all duration-500 ${
                faq.reveal.isVisible ? `animate-fade-in-up stagger-${index + 1}` : 'opacity-0'
              } ${openIndex === index ? 'ring-2 ring-purple-500' : ''}`}
            >
              <button
                onClick={() => toggleFAQ(index)}
                className="w-full px-8 py-6 flex items-center justify-between text-left hover:bg-purple-50/50 transition-all duration-300 cursor-pointer group relative overflow-hidden"
              >
                {/* Subtle glow on hover */}
                <div className="absolute inset-0 bg-gradient-to-r from-purple-500/5 to-pink-500/5 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>

                <span className="text-lg font-bold text-gray-900 pr-4 relative z-10">
                  {faq.question}
                </span>
                
                {/* Animated icon */}
                <div className="relative z-10">
                  <div className={`w-10 h-10 flex items-center justify-center bg-gradient-to-br from-purple-500 to-pink-500 rounded-full text-white transition-all duration-500 ${
                    openIndex === index ? 'rotate-180 scale-110' : 'rotate-0 scale-100'
                  }`}>
                    <i className={`ri-arrow-down-s-line text-2xl transition-transform duration-500`}></i>
                  </div>
                </div>
              </button>

              {/* Answer with smooth spring animation */}
              <div
                className={`overflow-hidden transition-all duration-500 ease-out ${
                  openIndex === index ? 'max-h-96 opacity-100' : 'max-h-0 opacity-0'
                }`}
                style={{
                  transitionTimingFunction: openIndex === index 
                    ? 'cubic-bezier(0.34, 1.56, 0.64, 1)' // Spring easing
                    : 'cubic-bezier(0.4, 0, 0.2, 1)',
                }}
              >
                <div className="px-8 pb-6 pt-2">
                  <div className="border-t border-gray-100 pt-4">
                    <p className="text-gray-700 leading-relaxed">
                      {faq.answer}
                    </p>
                  </div>
                </div>
              </div>

              {/* Bottom accent line */}
              <div className={`h-1 bg-gradient-to-r from-purple-500 to-pink-500 transform transition-all duration-500 origin-left ${
                openIndex === index ? 'scale-x-100' : 'scale-x-0'
              }`}></div>
            </div>
          ))}
        </div>

        {/* Bottom CTA */}
        <div className="mt-16 text-center">
          <p className="text-gray-600 mb-6">
            Neradote atsakymo į savo klausimą?
          </p>
          <a
            href="mailto:giedrius@giedriuspetrauskas.com"
            className="inline-flex items-center gap-3 bg-gradient-to-r from-purple-600 to-pink-600 text-white px-8 py-4 rounded-full font-bold text-lg hover:from-purple-700 hover:to-pink-700 transition-all shadow-lg hover:shadow-2xl whitespace-nowrap cursor-pointer group"
          >
            <i className="ri-mail-line text-xl"></i>
            <span>Susisiekite Su Mumis</span>
            <i className="ri-arrow-right-line text-xl group-hover:translate-x-1 transition-transform"></i>
          </a>
        </div>
      </div>
    </section>
  );
}
